import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { RATE_TYPE_LABELS, useAcknowledgeRateReview, useRateEvidenceEntries, useRateObservations, useSelectRate } from '../../../pages/Delivery/Registries/hooks';
const REVIEW_COPY = {
    CORRECTED: 'The pinned rate was corrected',
    INVALIDATED: 'The pinned rate was invalidated',
    NEWER_EVIDENCE: 'Newer rate evidence exists'
};
const usd = (n)=>`$${n.toFixed(2)}`;
// What the consumer is holding: the frozen pick, and — only when the Rate Book
// has moved — an offer to review. Neither button here edits a source rate, and
// the selected amount stays put until the person chooses.
export function PinnedSelection({ projectId, companyId, scenarioId, category, selection, onApply }) {
    const ack = useAcknowledgeRateReview(projectId, scenarioId);
    const select = useSelectRate(projectId, scenarioId);
    const review = selection.reviewStatus;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-1 space-y-1 text-xs",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": `${Testids.rateSelection.pinned}-${category.id}`,
                className: "text-muted-foreground",
                children: [
                    "Pinned ",
                    usd(selection.selectedAmount),
                    " · observation v",
                    selection.observationVersion,
                    " (",
                    usd(selection.observationAmount),
                    ")"
                ]
            }),
            review && /*#__PURE__*/ _jsxs("div", {
                "data-testid": `${Testids.rateSelection.reviewBanner}-${category.id}`,
                className: "rounded border border-warning bg-warning-muted p-2 text-warning",
                children: [
                    REVIEW_COPY[review] ?? 'Review rate evidence',
                    selection.reviewCandidateId && ` — v${selection.reviewCandidateVersion} at ${usd(selection.reviewCandidateAmount)}`,
                    ". Your selected amount has not changed.",
                    selection.reviewCandidateId && /*#__PURE__*/ _jsxs("span", {
                        className: "ml-2 inline-flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                disabled: ack.isPending,
                                "data-testid": `${Testids.rateSelection.keepReview}-${category.id}`,
                                onClick: ()=>ack.mutate({
                                        companyId,
                                        selectionId: selection.id
                                    }),
                                children: "Keep my selection"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                disabled: select.isPending,
                                "data-testid": `${Testids.rateSelection.adoptReview}-${category.id}`,
                                onClick: ()=>select.mutate({
                                        companyId,
                                        consumerRef: category.id,
                                        observationId: selection.reviewCandidateId
                                    }, {
                                        onSuccess: (s)=>s && onApply(s.selectedAmount)
                                    }),
                                children: "Use the new rate"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
// Inspect → compare → deliberately select. Opening this panel reads only.
export function RateEvidencePanel({ projectId, companyId, scenarioId, category, onApply }) {
    const entries = useRateEvidenceEntries(companyId);
    const [entryId, setEntryId] = useState('');
    const [adjust, setAdjust] = useState({});
    const observations = useRateObservations(companyId, {
        lcatEntryId: entryId
    }, {
        enabled: !!entryId
    });
    const select = useSelectRate(projectId, scenarioId);
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.rateSelection.panel,
        className: "space-y-2 rounded border border-border p-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "text-sm font-medium text-foreground",
                children: [
                    "Rate evidence for ",
                    category.title
                ]
            }),
            /*#__PURE__*/ _jsxs(Select, {
                value: entryId,
                onValueChange: setEntryId,
                children: [
                    /*#__PURE__*/ _jsx(SelectTrigger, {
                        className: "w-72",
                        "aria-label": "Registry labor category",
                        "data-testid": Testids.rateSelection.lcatPicker,
                        children: /*#__PURE__*/ _jsx(SelectValue, {
                            placeholder: "Choose a registry labor category"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SelectContent, {
                        children: (entries.data ?? []).map((e)=>/*#__PURE__*/ _jsx(SelectItem, {
                                value: e.id,
                                children: e.title
                            }, e.id))
                    })
                ]
            }),
            (observations.data ?? []).map((o)=>{
                const hourly = o.rateUnit === 'HOURLY';
                return /*#__PURE__*/ _jsxs("div", {
                    "data-testid": `${Testids.rateSelection.observationRow}-${o.id}`,
                    className: "flex flex-wrap items-center gap-3 border-t border-border pt-2 text-xs",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBadge, {
                            children: RATE_TYPE_LABELS[o.observationType] ?? o.observationType
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium",
                            children: usd(o.amount)
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: o.rateUnit.toLowerCase()
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: o.vehicleName || 'no contract linked'
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: [
                                o.worksite,
                                o.locality
                            ].filter(Boolean).join(' · ') || 'worksite not stated'
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: o.periodLabel || 'period not stated'
                        }),
                        hourly ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    className: "w-24",
                                    placeholder: "Adjust",
                                    "aria-label": "Adjusted amount",
                                    "data-testid": `${Testids.rateSelection.adjustInput}-${o.id}`,
                                    value: adjust[o.id] ?? '',
                                    onChange: (e)=>setAdjust({
                                            ...adjust,
                                            [o.id]: e.target.value
                                        })
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    disabled: select.isPending,
                                    "data-testid": `${Testids.rateSelection.selectButton}-${o.id}`,
                                    onClick: ()=>select.mutate({
                                            companyId,
                                            consumerRef: category.id,
                                            observationId: o.id,
                                            selectedAmount: Number(adjust[o.id]) || 0
                                        }, {
                                            onSuccess: (s)=>s && onApply(s.selectedAmount)
                                        }),
                                    children: "Use for this bid"
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx("span", {
                            "data-testid": `${Testids.rateSelection.notHourly}-${o.id}`,
                            className: "text-muted-foreground",
                            children: "Not an hourly rate — cannot be selected"
                        })
                    ]
                }, o.id);
            }),
            select.isError && /*#__PURE__*/ _jsx("div", {
                className: "text-xs text-danger",
                children: String(select.error)
            })
        ]
    });
}
